import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { EcmRouterModule, Link, LinkService, TrackingData } from '@ecm/router';

import { UniverseEnum } from '../../../../business/api/dashboard/universeEnum';
import { ActiveContractViewModel } from '../../../../business/model/crossroads/contracts-options/active-contract.view-model';
import { ContractsViewModel } from '../../../../business/model/crossroads/contracts.view-model';
import { TrackedPage } from '../../../../business/model/tracking/trackedPage.enum';
import { ParamsUtils } from '../../../../utils/params.utils';
import { AbstractComponent } from '../../../shared/abstract.component';
import { CrossroadSuspendedAlertComponent } from '../../contract/crossroadSuspendedAlert/crossroadSuspendedAlert.component';
import { CrossroadsUtils } from '../../utils/crossroads.utils';

@Component({
    selector: 'ecm-bill-item',
    templateUrl: 'active-bill.html',
    styleUrls: ['active-bill.css'],
    standalone: true,
    imports: [NgClass, CrossroadSuspendedAlertComponent, AsyncPipe, DatePipe, EcmRouterModule, TranslateModule],
})
export class ActiveBillComponent extends AbstractComponent implements OnInit {
    @Input()
    public viewModel: ContractsViewModel;

    @Input()
    public isVimEligible: boolean;

    @Input()
    public hideBorder: boolean;

    @Input()
    public universe: UniverseEnum;

    public mixedSoshPicto = 'assets/dashboard/images/mini-logo-sosh.svg';
    public mixedOrangePicto = 'assets/dashboard/images/mini-logo-orange.png';
    public icon: string;
    public activeContractViewModel: ActiveContractViewModel;

    private readonly billPageLinkUrl: string = 'facture-paiement/{{cid}}';

    public readonly ParamsUtils = ParamsUtils;

    public billLink: Link;

    public alertTheme = 'alert-warning';
    public iconTheme = 'icon-Warning-important';

    public labelButton: string;
    public inprogressLinkLabel: string;

    /**
     * GA tracking area
     * @type {string}
     */
    private readonly trackingArea: string = 'care_selectionfacture_factures';

    private readonly translate = inject(TranslateService);
    private readonly linkService = inject(LinkService);

    public constructor() {
        super();
        this.translate.use('fr');
    }

    public ngOnInit(): void {
        super.ngOnInit();
        if (this.viewModel.contract) {
            this.activeContractViewModel = this.viewModel.contract as ActiveContractViewModel;
        }
        if (this.activeContractViewModel.koContract && !this.activeContractViewModel.koContract.isWarningAlert) {
            this.alertTheme = 'alert-info';
            this.iconTheme = 'icon-info';
        }
        if (this.universe === UniverseEnum.MIXED) {
            this.icon =
                CrossroadsUtils.getIconBrand(this.universe, this.viewModel, this.activeContractViewModel) ===
                UniverseEnum.SOSH
                    ? this.mixedSoshPicto
                    : this.mixedOrangePicto;
        }
        this.billLink = { inApp: this.billPageLinkUrl };
        // Get brand flag and translations synchronously
        const dashboardBillsTranslate = this.translate.instant('dashboard.bills');
        const isSosh = this.isSoshBrand()();
        this.labelButton = this.getLabelButton(isSosh, dashboardBillsTranslate['button']);
        if (this.activeContractViewModel.koContract?.hideButtonKoContract) {
            this.inprogressLinkLabel = this.getInprogressLinkLabel(isSosh);
        }
    }

    public getLabelButton(isSosh: boolean, billsButton: any): string {
        if (!isSosh && this.activeContractViewModel.brand === 'Sosh') {
            return billsButton['soshLabel'];
        } else if (isSosh && this.activeContractViewModel.brand === 'Orange') {
            return billsButton['orangeLabel'];
        } else {
            return billsButton['paidYourBill'];
        }
    }

    public getInprogressLinkLabel(isSosh: boolean): string {
        let linkLabel: string = this.activeContractViewModel.koContract.linkLabel;
        if (this.universe === UniverseEnum.MIXED) {
            if (isSosh && this.viewModel.brand.toUpperCase() === UniverseEnum.ORANGE) {
                linkLabel = this.activeContractViewModel.koContract.orangeLinkLabel;
            } else if (!isSosh && this.viewModel.brand.toUpperCase() === UniverseEnum.SOSH) {
                linkLabel = this.activeContractViewModel.koContract.soshLinkLabel;
            }
        }
        return linkLabel;
    }

    /**
     * Open inprogress link
     */
    public openInprogressLink(): void {
        this.linkService.openLink(
            this.activeContractViewModel.koContract.link,
            Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.bills,
                    trackingArea: this.trackingArea,
                    trackingName: this.activeContractViewModel.koContract.linkLabel,
                },
            }),
        );
    }

    /**
     * Go to contract bill page
     */
    public openBill(): void {
        const isSosh = this.isSoshBrand()();
        const button = this.translate.instant('dashboard.bills.button');
        const link: Link = button['billPageLinkUrl'];
        let navigationExtras: NavigationExtras;
        let trackingNameParam: string = button['paidYourBill'];
        if (!isSosh && this.viewModel.brand.toUpperCase() === UniverseEnum.SOSH) {
            navigationExtras = { queryParams: { sosh: '' } };
            trackingNameParam = button['soshLabel'];
        } else if (isSosh && this.viewModel.brand.toUpperCase() === UniverseEnum.ORANGE) {
            navigationExtras = { queryParams: { orange: '' } };
            trackingNameParam = button['orangeLabel'];
        } else {
            navigationExtras = null;
        }
        this.linkService.openLink(
            link,
            Object.assign(new TrackingData(), {
                tmsTracking: {
                    trackedPage: TrackedPage.bills,
                    trackingArea: this.trackingArea,
                    trackingName: trackingNameParam,
                },
            }),
            ParamsUtils.cidMap(this.activeContractViewModel.cid),
            navigationExtras,
        );
    }

    /**
     * Open family advantage
     */
    public openWhoUseContract(): void {
        this.translate.get('dashboard.bills.whoUseContract').subscribe((whoUseContract: any) => {
            this.linkService.openLink(
                whoUseContract['link'],
                Object.assign(new TrackingData(), {
                    tmsTracking: {
                        trackedPage: TrackedPage.bills,
                        trackingArea: this.trackingArea,
                        trackingName: whoUseContract['label'],
                    },
                }),
                ParamsUtils.cidMap(this.activeContractViewModel.cid),
                whoUseContract['linkParam'],
            );
        });
    }
}
